import moment from "moment";

type DateFormatOptions = "MM/DD/YYYY" | "DD/MM/YYYY" | "YYYY-MM-DD";
type TimeFormatOptions = "HH:mm:ss" | "h:mm:ss A" | "HH:mm:ss.SSS";

interface GetDateFormatOptions {
  isMoment?: boolean;
  includeTime?: boolean;
  includeFractionalSecs?: boolean;
}

// union type for monolith-ui date format
export type DateInputFormat =
  | DateFormatOptions
  | `${DateFormatOptions} ${TimeFormatOptions}`;

export const DEFAULT_DATE_FORMAT = "YYYY-MM-DD";
export const DEFAULT_TIME_FORMAT = "HH:mm:ss";

// Utility function to allow users to set their own date format
export function getDateFormat({
  isMoment = true,
  includeTime = true,
  includeFractionalSecs = false,
}: GetDateFormatOptions = {}): string {
  // Adding allowing string type for now in case current customers have an invalid date type in local storage
  const dateFormat: DateFormatOptions | string =
    localStorage.getItem("dateFormat.date") || DEFAULT_DATE_FORMAT;
  const timeFormat: TimeFormatOptions | string =
    localStorage.getItem("dateFormat.time") || DEFAULT_TIME_FORMAT;

  // Return format compatible with moment.js
  if (isMoment)
    return (
      dateFormat +
      (includeTime ? ` ${timeFormat}` : "") +
      (includeFractionalSecs ? ".SSS" : "")
    );

  // Return format compatible with JS Date
  const jsDateFormat =
    {
      "YYYY-MM-DD": "yyyy-MM-dd",
      "DD/MM/YYYY": "dd/MM/yyyy",
      "MM/DD/YYYY": "MM/dd/yyyy",
    }[dateFormat] || "yyyy-MM-dd";

  if (includeTime) {
    return `${jsDateFormat} ${timeFormat.replace("A", "a")}`;
  }
  return jsDateFormat;
}

interface MonolithMomentOptions {
  timestamp?: string | number | Date;
  includeTime?: boolean;
  includeZone?: boolean;
  // true: always show milliseconds (missing ms render as .000)
  // "auto": show milliseconds only when the timestamp has a non-zero ms component
  includeMs?: boolean | "auto";
  format?: string;
}

function hasMilliseconds(timestamp: string | number | Date): boolean {
  return moment.utc(timestamp).milliseconds() !== 0;
}

function withSeconds(timeFormat: string): string {
  if (timeFormat.includes(":ss")) return timeFormat;
  if (timeFormat.includes(":mm")) return timeFormat.replace(":mm", ":mm:ss");
  return `${timeFormat}:ss`;
}

function withMsPrecision(timeFormat: string): string {
  const format = withSeconds(timeFormat);
  if (format.includes(".SSS")) return format;
  return format.replace(":ss", ":ss.SSS");
}

//convert from UTC to local time - for displaying times in Monolith
export function monolithMoment(options: MonolithMomentOptions = {}): string {
  if (!options.timestamp) return "";
  const isString = typeof options.timestamp === "string";
  const isDateOnly = isString && (options.timestamp as string).length < 11;

  const dateFormat =
    localStorage.getItem("dateFormat.date") || DEFAULT_DATE_FORMAT;

  if (isDateOnly) {
    return moment(options.timestamp).format(dateFormat);
  }

  const timeFormat = withSeconds(
    localStorage.getItem("dateFormat.time") || DEFAULT_TIME_FORMAT
  );

  const showMs =
    options.includeMs === true ||
    (options.includeMs === "auto" && hasMilliseconds(options.timestamp));

  const format = [
    dateFormat,
    options.includeTime || showMs
      ? showMs
        ? withMsPrecision(timeFormat)
        : timeFormat
      : "",
    options.includeZone ? "ZZ" : "",
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  const finalFormat = options.format || format;

  return moment.utc(options.timestamp).local().format(finalFormat);
}

//convert to UTC - used to store timestamps in db
export function db_timestamp(
  timestamp?: string | number | Date,
  isISO = false
): string {
  const momentObj = timestamp ? moment(timestamp) : moment();
  return isISO
    ? momentObj.toISOString()
    : momentObj.utc().format("YYYY-MM-DD HH:mm:ss");
}

interface TimestampValidation {
  value: string | number | Date;
}

export function validateTimestamp(timestamp: TimestampValidation): boolean {
  return timestamp.value
    ? parseInt(moment(timestamp.value).format("YYYY"), 10) > 1900
    : true;
}
